import { defineMessages } from "react-intl";

/** Section-level strings: page titles the views own, and the delete dialogs. */
export const sectionMessages = defineMessages({
  groupListTitle: {
    defaultMessage: "Dealer Pricing", id: 'iy/eiG',
    description: "dealer group list, page title",
  },
  groupCreateTitle: {
    defaultMessage: "Create dealer group", id: 'JBOWzw',
    description: "dealer group create, page title",
  },
  customerListTitle: {
    defaultMessage: "Dealer customers", id: 'w/8Lr4',
    description: "dealer customer list, page title",
  },
  customerAssignTitle: {
    defaultMessage: "Assign a dealer", id: 'B58wqS',
    description: "dealer customer assign, page title",
  },
  settingsTitle: {
    defaultMessage: "Dealer pricing settings", id: 'Qb1kdH',
    description: "dealer settings, page title",
  },
  groupDeleteTitle: {
    defaultMessage: "Delete dealer group", id: 'XDp0D6',
    description: "dealer group detail, dialog title",
  },
  groupDeleteContent: {
    defaultMessage:
      "Delete {name}? Its {count, plural, one {# tier price} other {# tier prices}} go with it. A group with customers in it cannot be deleted.", id: '4f4T9d',
    description: "dealer group detail, dialog content",
  },
  unassignTitle: {
    defaultMessage: "Remove from dealer group", id: 'xBd2eW',
    description: "dealer customer detail, dialog title",
  },
  unassignContent: {
    defaultMessage:
      "{name} goes back to retail pricing. The group keeps its tier prices.", id: 'Xqn/On',
    description: "dealer customer detail, dialog content",
  },
});

/** The group list and the group form. */
export const groupMessages = defineMessages({
  createGroup: { defaultMessage: "Create group", id: 'BecC4P', description: "dealer group list, button" },
  searchPlaceholder: {
    defaultMessage: "Search dealer groups...", id: 'gX5Hr2',
    description: "dealer group list, search field",
  },
  noGroups: {
    defaultMessage: "No dealer group yet", id: 'rfZ+LN',
    description: "dealer group list, empty state",
  },
  colCode: { defaultMessage: "Code", id: 'wMKrko', description: "dealer group list column" },
  colName: { defaultMessage: "Name", id: 'bPqCtp', description: "dealer group list column" },
  colCustomers: { defaultMessage: "Customers", id: 'NMdW8L', description: "dealer group list column" },
  colTierPrices: { defaultMessage: "Tier prices", id: 'OjFiOR', description: "dealer group list column" },
  generalTitle: { defaultMessage: "Dealer group", id: '2IgvHD', description: "dealer group detail, card title" },
  fieldCode: { defaultMessage: "Code", id: 'RGlv24', description: "dealer group detail, field" },
  fieldCodeHelp: {
    defaultMessage:
      "Prices and your storefront refer to this group by its code. Do not change it once prices are loaded.", id: '+nLG4y',
    description: "dealer group detail, field help",
  },
  fieldName: { defaultMessage: "Name", id: '+m8Gbr', description: "dealer group detail, field" },
  fieldNameHelp: {
    defaultMessage: "If this is empty, staff see the code instead.", id: 'agfVGn',
    description: "dealer group detail, field help",
  },
});

/** The tier-price grid inside the group detail. */
export const tierMessages = defineMessages({
  title: {
    defaultMessage: "Tier prices ({count})", id: 'AHXVYD',
    description: "dealer group detail, grid title",
  },
  addPrice: { defaultMessage: "Add price", id: 'nCRpy4', description: "dealer tier grid, button" },
  pastePrices: { defaultMessage: "Paste prices", id: 'QUseZt', description: "dealer tier grid, button" },
  removeRow: { defaultMessage: "Remove", id: 'svkwlv', description: "dealer tier grid, row menu" },
  removeSelected: { defaultMessage: "Remove", id: 'wQs+U5', description: "dealer tier grid, selection action" },
  colProduct: { defaultMessage: "Product", id: 'EpFVAP', description: "dealer tier grid column" },
  colSku: { defaultMessage: "SKU", id: 'HTMBUx', description: "dealer tier grid column" },
  colMinQuantity: {
    defaultMessage: "From quantity", id: 'ob4tO3',
    description: "dealer tier grid column",
  },
  colAmount: { defaultMessage: "Price each ({currency})", id: '5XupJV', description: "dealer tier grid column" },
  colAmountNoCurrency: { defaultMessage: "Price each", id: '3dLb5i', description: "dealer tier grid column" },
  empty: {
    defaultMessage: "No prices yet. Add one, or paste a price list.", id: 'i42+gB',
    description: "dealer tier grid, empty state",
  },
  unsaved: {
    defaultMessage: "{count, plural, one {# unsaved row} other {# unsaved rows}}", id: 'a3IlDz',
    description: "dealer tier grid, pending changes",
  },
  truncated: {
    defaultMessage:
      "Showing the first {count} prices in this group. The rest are untouched by this screen.", id: '+71yCD',
    description: "dealer tier grid, load cap",
  },
});

/** The paste importer. */
export const pasteMessages = defineMessages({
  title: { defaultMessage: "Paste prices", id: '2TCPHB', description: "dealer paste dialog, title" },
  body: {
    defaultMessage:
      "One line per price, SKU then price, separated by a tab or a comma. Paste straight from a spreadsheet.", id: '43pQp8',
    description: "dealer paste dialog, help",
  },
  placeholder: {
    defaultMessage: "350-CW,260.00\nT2-BT,119.99", id: 'B2MgDr',
    description: "dealer paste dialog, textarea placeholder",
  },
  minQuantityLabel: {
    defaultMessage: "Applies from quantity", id: 'hsQc71',
    description: "dealer paste dialog, field",
  },
  parsed: {
    defaultMessage: "{count, plural, one {# price} other {# prices}} found", id: 'kDc6qb',
    description: "dealer paste dialog, parse result",
  },
  confirm: {
    defaultMessage: "Add {count, plural, one {# price} other {# prices}}", id: 'YnZGkS',
    description: "dealer paste dialog, button",
  },
  confirmEmpty: { defaultMessage: "Add prices", id: 'cczziv', description: "dealer paste dialog, button" },
  cap: {
    defaultMessage:
      "One save adds at most {count} prices, and this grid already has {pending} waiting to be saved. Save what is here, or take some lines out.", id: 'ZCtLUG',
    description: "dealer paste dialog, row cap",
  },
  lookupFailed: {
    defaultMessage: "Those SKUs could not be looked up. Nothing has been added.", id: '7PlH2s',
    description: "dealer paste dialog, lookup failed",
  },
  retry: {
    defaultMessage: "Try again", id: 'rAheP/',
    description: "dealer paste dialog, retry the SKU lookup",
  },
  refusedTitle: {
    defaultMessage: "{count, plural, one {# line} other {# lines}} could not be read", id: '5RqAcS',
    description: "dealer paste dialog, refusals",
  },
  colLine: { defaultMessage: "Line", id: 'Q8SK+P', description: "dealer paste dialog column" },
  colContent: { defaultMessage: "Pasted", id: 'JR8Ze7', description: "dealer paste dialog column" },
  colProblem: { defaultMessage: "Problem", id: 'JQrEV0', description: "dealer paste dialog column" },
  reasonColumns: {
    defaultMessage: "Needs a SKU and a price, separated by a tab or a comma", id: 'G3lbhJ',
    description: "dealer paste dialog, refusal reason",
  },
  reasonSku: {
    defaultMessage: "No SKU on this line", id: 'jKI5oc',
    description: "dealer paste dialog, refusal reason",
  },
  reasonAmount: {
    defaultMessage: "That price is not a number", id: 'iMBuKm',
    description: "dealer paste dialog, refusal reason",
  },
  reasonUnknownSku: {
    defaultMessage: "No product with that SKU", id: 'V3r5pE',
    description: "dealer paste dialog, refusal reason",
  },
  resolving: {
    defaultMessage: "Looking up SKUs...", id: 'FYnG44',
    description: "dealer paste dialog, in flight",
  },
});

/** The dealer customer list and the assignment form. */
export const customerMessages = defineMessages({
  assignCustomer: { defaultMessage: "Assign customer", id: 'qQv7sS', description: "dealer customer list, button" },
  searchPlaceholder: {
    defaultMessage: "Search dealer customers...", id: 'seK14a',
    description: "dealer customer list, search field",
  },
  noCustomers: {
    defaultMessage: "No customer is on dealer pricing yet", id: 'mOW0E5',
    description: "dealer customer list, empty state",
  },
  colCustomer: { defaultMessage: "Customer", id: 'ivoqOb', description: "dealer customer list column" },
  colEmail: { defaultMessage: "Email", id: 'GhWsbc', description: "dealer customer list column" },
  colGroup: { defaultMessage: "Dealer group", id: 'wv5V20', description: "dealer customer list column" },
  colTaxExempt: { defaultMessage: "Tax exempt", id: 'KlyOdX', description: "dealer customer list column" },
  taxExemptYes: { defaultMessage: "Yes", id: 'UtPUN6', description: "dealer customer list, tax exempt" },
  taxExemptNo: { defaultMessage: "No", id: 'mugvuk', description: "dealer customer list, tax exempt" },
  generalTitle: {
    defaultMessage: "Dealer", id: 'tttLr6',
    description: "dealer customer detail, card title",
  },
  customerField: {
    defaultMessage: "Customer", id: 'rGgHpr',
    description: "dealer customer detail, field",
  },
  customerFieldHelp: {
    defaultMessage: "The customer cannot be changed later. Create a new record instead.", id: 'WsbPpZ',
    description: "dealer customer detail, field help",
  },
  groupField: { defaultMessage: "Dealer group", id: 'T0DBHM', description: "dealer customer detail, field" },
  groupFieldHelp: {
    defaultMessage: "The group whose tier prices this customer pays.", id: 'OBhx+X',
    description: "dealer customer detail, field help",
  },
  taxExempt: { defaultMessage: "Tax exempt", id: 'WhoGvG', description: "dealer customer detail, field" },
  taxExemptHelp: {
    defaultMessage: "Wired to the checkout's own tax exemption.", id: 'WroDeh',
    description: "dealer customer detail, field help",
  },
  unassign: { defaultMessage: "Remove from group", id: '9USUsO', description: "dealer customer detail, button" },
});

/** The settings singleton. */
export const settingsMessages = defineMessages({
  cardTitle: { defaultMessage: "Discounts", id: 'fq4zCq', description: "dealer settings, card title" },
  discountStacking: {
    defaultMessage: "Let discounts stack on dealer prices", id: 'bgi7Jb',
    description: "dealer settings, field",
  },
  discountStackingHelp: {
    defaultMessage:
      "When this is off (the default), an item already at a dealer price gets no voucher, promotion or order discount on top.", id: 'JSu+O+',
    description: "dealer settings, field help",
  },
});

/** The right rails: what the stock detail layout puts beside the form. */
export const railMessages = defineMessages({
  groupTitle: { defaultMessage: "This group", id: 's1CohC', description: "dealer group rail, title" },
  customers: { defaultMessage: "Customers", id: 'qb2ol0', description: "dealer group rail, count" },
  tierPrices: { defaultMessage: "Tier prices", id: 'ca+2to', description: "dealer group rail, count" },
  viewCustomers: {
    defaultMessage: "See this group's customers", id: 'Sun4U0',
    description: "dealer group rail, link",
  },
  settingsTitle: {
    defaultMessage: "How dealer pricing works", id: 'cL7HEd',
    description: "dealer settings rail, title",
  },
  settingsBody: {
    defaultMessage:
      "A dealer group holds the prices. A customer put in that group pays them from their next order. These settings apply to every group.", id: '4N7WhY',
    description: "dealer settings rail, body",
  },
  customerTitle: {
    defaultMessage: "Their dealer group", id: 'QDcWp+',
    description: "dealer customer rail, title",
  },
  openGroup: {
    defaultMessage: "Open this dealer group", id: 'mriU7v',
    description: "dealer customer rail, link",
  },
  filteredByGroup: {
    defaultMessage: "Showing the customers in {group}.", id: 'tbXXR1',
    description: "dealer customer list, group filter",
  },
});
